import { ZoomIn } from 'lucide-react';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next'; // <-- NEU
import type { DragItem, OrderedPage } from '../utils.js';

interface PageThumbnailProps {
  page: OrderedPage;
  idx: number;
  bucketId: string;
  isReadOnly?: boolean;
  fallbackUrl?: string;
  draggedItem: DragItem | null;
  dropTarget: DragItem | null;
  onDragStart: (e: React.DragEvent, bucketId: string, index: number) => void;
  onDragOver: (e: React.DragEvent, bucketId: string, index: number) => void;
  onDrop: (e: React.DragEvent, bucketId: string, index: number) => void;
  onPreview: (bucketId: string, index: number) => void;
}

export function PageThumbnail({
  page,
  idx,
  bucketId,
  isReadOnly = false,
  fallbackUrl,
  draggedItem,
  dropTarget,
  onDragStart,
  onDragOver,
  onDrop,
  onPreview,
}: PageThumbnailProps) {
  const { t } = useTranslation(); // <-- NEU
  const isDragging = draggedItem?.bucketId === bucketId && draggedItem?.index === idx;
  const isDropTarget = dropTarget?.bucketId === bucketId && dropTarget?.index === idx;
  const url = page.url || fallbackUrl;
  const draggingRef = useRef(false);

  const openPreview = () => {
    if (!url || draggingRef.current) return;
    onPreview(bucketId, idx);
  };

  return (
    <div
      draggable={!isReadOnly}
      onDragStart={e => {
        if (isReadOnly) return;
        draggingRef.current = true;
        onDragStart(e, bucketId, idx);
      }}
      onDragEnd={() => {
        setTimeout(() => {
          draggingRef.current = false;
        }, 0);
      }}
      onDragOver={e => {
        if (isReadOnly) return;
        e.stopPropagation();
        onDragOver(e, bucketId, idx);
      }}
      onDrop={e => {
        if (isReadOnly) return;
        e.stopPropagation();
        onDrop(e, bucketId, idx);
      }}
      className={`group relative w-[110px] h-[155px] shrink-0 rounded-lg border shadow-sm bg-white dark:bg-slate-800 overflow-hidden transition-all ${
        isReadOnly ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'
      } ${
        isDragging ? 'opacity-30 scale-95' : 'hover:border-blue-400 hover:shadow-md'
      } ${isDropTarget ? 'border-l-4 border-l-blue-500 ml-2' : 'border-slate-200 dark:border-slate-700'}`}
    >
      <div className="absolute top-1 left-1 right-1 p-0.5 flex justify-between items-center z-10 pointer-events-none">
        <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300 tracking-wider px-1.5 py-0.5 bg-white/90 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded">
          {t('Canvas.submissionCheck.pageShort', { page: idx + 1 })}
        </span>
      </div>

      {url && (
        <button
          type="button"
          onClick={event => {
            event.stopPropagation();
            openPreview();
          }}
          onMouseDown={event => event.stopPropagation()}
          className="absolute top-1 right-1 z-20 inline-flex items-center justify-center w-6 h-6 bg-white/95 dark:bg-slate-800/95 border border-slate-200 dark:border-slate-700 rounded text-slate-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-700 transition"
          title={t('Canvas.submissionCheck.zoomPage', { page: idx + 1 })}
          aria-label={t('Canvas.submissionCheck.zoomPage', { page: idx + 1 })}
        >
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
      )}

      {url ? (
        <img
          src={url}
          alt={t('Canvas.submissionCheck.pageAlt')}
          className="w-full h-full object-cover select-none pointer-events-none"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-slate-100 dark:bg-slate-700 text-xs text-slate-400 dark:text-slate-500">
          {t('Canvas.submissionCheck.loading')}
        </div>
      )}
    </div>
  );
}
